/**
 * Pure helpers behind the chat settings modal's email section: the composite
 * value the account `<select>` carries, and validation of the
 * `/email-send-context` payload.
 *
 * The payload crosses a network boundary, so it is PARSED rather than cast
 * (repo rule main-03). A malformed entry is dropped instead of being trusted
 * into the option list, where a missing `connectionId` used to render an
 * option whose value was the literal string "undefined|google-mail".
 */

export interface EmailConnection {
  connectionId: string;
  provider: string;
  providerConfigKey?: string;
  email?: string | null;
}

export interface SignatureOption {
  id?: string;
  name?: string;
  is_default?: boolean;
}

export interface DefaultEmailPreference {
  connectionId: string;
  provider: string;
}

export interface EmailSendContext {
  connections: EmailConnection[];
  signaturesByConnectionId: Record<string, SignatureOption[]>;
  defaultPreference: DefaultEmailPreference | null;
  googleCalendarUseSecondAccount: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

export function getProviderLabel(provider: string): string {
  return provider === 'google-mail' ? 'Gmail' : provider === 'outlook-oauth' ? 'Outlook' : provider;
}

/** The composite `<option value>`: connection and provider travel together. */
export function connectionValue(conn: DefaultEmailPreference): string {
  return `${conn.connectionId}|${conn.provider}`;
}

export function parseConnectionValue(value: string): DefaultEmailPreference | null {
  const [connectionId, provider] = value.split('|');
  return connectionId && provider ? { connectionId, provider } : null;
}

function parseConnection(raw: unknown): EmailConnection | null {
  if (!isRecord(raw)) return null;
  const connectionId = optionalString(raw.connectionId);
  const provider = optionalString(raw.provider);
  if (!connectionId || !provider) return null;
  return {
    connectionId,
    provider,
    providerConfigKey: optionalString(raw.providerConfigKey),
    email: typeof raw.email === 'string' ? raw.email : null,
  };
}

function parseSignature(raw: unknown): SignatureOption | null {
  if (!isRecord(raw)) return null;
  return {
    id: optionalString(raw.id),
    name: optionalString(raw.name),
    is_default: raw.is_default === true,
  };
}

function parseSignatureMap(raw: unknown): Record<string, SignatureOption[]> {
  if (!isRecord(raw)) return {};
  const out: Record<string, SignatureOption[]> = {};
  for (const [connectionId, value] of Object.entries(raw)) {
    if (!Array.isArray(value)) continue;
    out[connectionId] = value
      .map(parseSignature)
      .filter((s): s is SignatureOption => s !== null);
  }
  return out;
}

export function parseEmailSendContext(raw: unknown): EmailSendContext {
  const root = isRecord(raw) ? raw : {};
  const pref = isRecord(root.defaultPreference) ? root.defaultPreference : null;
  const prefConnectionId = pref ? optionalString(pref.connectionId) : undefined;
  const prefProvider = pref ? optionalString(pref.provider) : undefined;

  return {
    connections: Array.isArray(root.connections)
      ? root.connections.map(parseConnection).filter((c): c is EmailConnection => c !== null)
      : [],
    signaturesByConnectionId: parseSignatureMap(root.signaturesByConnectionId),
    defaultPreference:
      prefConnectionId && prefProvider
        ? { connectionId: prefConnectionId, provider: prefProvider }
        : null,
    googleCalendarUseSecondAccount: root.googleCalendarUseSecondAccount === true,
  };
}

/** The signature marked default for a connection, or '' when there is none. */
export function defaultSignatureId(signatures: readonly SignatureOption[] | undefined): string {
  if (!Array.isArray(signatures)) return '';
  return signatures.find((s) => s.is_default)?.id ?? '';
}

export interface DirtyStateInput {
  selectedValue: string;
  parsed: DefaultEmailPreference | null;
  defaultPreference: DefaultEmailPreference | null;
  selectedSignatureId: string;
  currentDefaultSignatureId: string;
  calendarUseSecondAccount: boolean;
  serverCalendarUseSecondAccount: boolean;
}

/**
 * Whether Save has anything to do. Three independent axes — the account, its
 * default signature, and the Meridian calendar account — because a user can
 * change any one of them alone.
 */
export function computeHasChanges({
  selectedValue,
  parsed,
  defaultPreference,
  selectedSignatureId,
  currentDefaultSignatureId,
  calendarUseSecondAccount,
  serverCalendarUseSecondAccount,
}: DirtyStateInput): boolean {
  const hasConnectionChanges =
    selectedValue === ''
      ? defaultPreference !== null
      : !defaultPreference ||
        defaultPreference.connectionId !== parsed?.connectionId ||
        defaultPreference.provider !== parsed?.provider;
  const hasSignatureChanges =
    !!parsed?.connectionId && (selectedSignatureId || '') !== (currentDefaultSignatureId || '');
  const hasCalendarChanges = calendarUseSecondAccount !== serverCalendarUseSecondAccount;
  return hasConnectionChanges || hasSignatureChanges || hasCalendarChanges;
}
